import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { thirteenMonthPayslip } from '../../../api/payroll';
const ThirteenthMonthPayslip = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [payslipData, setPayslipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('13th Month Pay Report');

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = useCallback(() => {
    window.print();
  }, [payslipData]);

  // function determineInconsistentCutoff(from, to) {
  //     const fromStr = from.toISOString().substring(0, 10);
  //     const toStr = to.toISOString().substring(0, 10);
  //     const dayFromString = fromStr.substring(8, 10)
  //     const dayToString = toStr.substring(8, 10)

  //     if (dayFromString === '26' && dayToString === '11') {
  //         return 'cutoff1';
  //     }
  //     else if (dayFromString === '01' && dayToString === '15') {
  //         return 'cutoff1';
  //     }
      
  //     return 'cutoff2';
  // }

  function determineCutoff(from, to) {
      const dayFromString = from.getDate();
      const dayToString = to.getDate();

      if ((dayFromString === 26 && dayToString === 10) || (dayFromString === 1 && dayToString === 15)) {
          return 'cutoff1';
      }

      // if (dayFromString <= 15) {
      //     return 'cutoff1';
      // }
      
      return 'cutoff2';
  }

  useEffect(() => {
    const fetchPayslipData = async () => {
      try {
        setLoading(true);

        const result = await thirteenMonthPayslip(employeeId);
        const summary = result.data.Summary;
        const summaryFormatted = summary.date_ranges.filter(
          (obj, index, self) =>
            index === self.findLastIndex(t => t.payroll_from === obj.payroll_from)
        );

        const thirteenthMonth = result.data.Thirteenthmonth;
        summaryFormatted.sort((a, b) => new Date(a.payroll_from) - new Date(b.payroll_from));
        const from = new Date(thirteenthMonth.from);
        const to = new Date(thirteenthMonth.to);
        const formattedDateCovered = `${from.toLocaleString('en-US', { month: 'long', year: 'numeric' })} - ${to.toLocaleString('en-US', { month: 'long', year: 'numeric' })}`;

        const breakdown = [];

        summaryFormatted.forEach(dr => {
          const from = new Date(dr.payroll_from);
          const to = new Date(dr.payroll_to);

          const month = to.toLocaleString('en-US', { month: 'long', year: 'numeric' });
          let monthEntry = breakdown.find(b => b.month === month);

          if (!monthEntry) {
            monthEntry = {
              month,
              cutoff1: '-',
              cutoff2: '-',
              monthTotal: 0
            };
            breakdown.push(monthEntry);
          }

          // const cutoffKey = from.getDate() <= 15 ? 'cutoff1' : 'cutoff2';
          const cutoffKeyV2 = determineCutoff(from, to);
          monthEntry[cutoffKeyV2] = dr.basic_pay;
          monthEntry.monthTotal += dr.basic_pay;
        });

        const totalBasicPay = breakdown.reduce((sum, m) => sum + m.monthTotal, 0);
        const thirteenthMonthPay = totalBasicPay / 12; // standard formula

        const mockPayslipData = {
          employee: {
            name: summary.employee_name,
            id: summary.employee_id,
          },
          assignment: {
            company: thirteenthMonth.name,
            dateCovered: formattedDateCovered,
          },
          breakdown,
          summary: {
            totalBasicPay: parseFloat(totalBasicPay.toFixed(2)),
            thirteenthMonthPay: parseFloat(thirteenthMonthPay.toFixed(2))
          }
        };

        setPayslipData(mockPayslipData);
        setCompanyName(thirteenthMonth.name);

      } catch (error) {
        console.error('Error fetching payslip:', error);
      } finally {
        setLoading(false);
      }
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get("print") === "true") {
        setTimeout(() => window.print(), 500);
      }
    };

    if (employeeId) {
      fetchPayslipData();
    } else {
      setError('Invalid record or employee ID');
      setLoading(false);
    }
  }, [employeeId]);

  // Listen for messages from parent window (dialog)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.action === 'download') {
        handleDownload();
      } else if (event.data && event.data.action === 'print') {
        handlePrint();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleDownload]);

  const generatePrintContent = () => {
    if (!payslipData) return '';

    const totalFirstCutoff = payslipData.breakdown
                .reduce((sum, row) => {
                  const val = (row.cutoff1 !== '-' && row.cutoff1 > 0) ? parseFloat(row.cutoff1) : 0;
                  return sum + val;
      }, 0);

    const totalSecondCutoff = payslipData.breakdown
                .reduce((sum, row) => {
                  const val = (row.cutoff2 !== '-' && row.cutoff2 > 0) ? parseFloat(row.cutoff2) : 0;
                  return sum + val;
      }, 0);

    const totalMonthTotal = payslipData.breakdown
      .reduce((sum, row) => sum + row.monthTotal, 0);

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>13th Month Payslip - ${payslipData.employee.name}</title>
          <style>
            @page {
              size: A4;
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              color: #333;
            }
            .payslip-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
            }
            .header {
              background: linear-gradient(135deg, #12C1DC, #0FABDB);
              color: white;
              padding: 20px;
              text-align: center;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: bold;
            }
            .employee-info {
              padding: 20px;
              background: #f8f9fa;
              border-bottom: 2px solid #e5e7eb;
            }
            .employee-info h2 {
              margin: 0 0 10px 0;
              color: #2c3e50;
              font-size: 20px;
            }
            .employee-info p {
              margin: 5px 0;
              color: #6b7280;
            }
            .payslip-content {
              padding: 20px;
            }
            .breakdown-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            .breakdown-table th {
              background: #12C1DC;
              color: white;
              padding: 10px;
              text-align: center;
              font-size: 14px;
              border: 1px solid #e5e7eb;
            }
            .breakdown-table td {
              padding: 8px 12px;
              border: 1px solid #e5e7eb;
              text-align: center;
            }
            .breakdown-table tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .breakdown-table tr:nth-child(odd) {
              background-color: #ffffff;
            }
            .breakdown-table tr:hover {
              background-color: #e9ecef !important;
            }
            .breakdown-table tr:last-child {
              background-color: #12C1DC !important;
              color: white;
              font-weight: bold;
            }
            .summary {
              background: #f8f9fa;
              padding: 20px;
              border-radius: 8px;
              margin: 20px 0;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .summary-row:last-child {
              border-bottom: none;
              font-weight: bold;
              font-size: 18px;
              color: #10b981;
            }
            .acknowledgement {
              margin-top: 30px;
              text-align: center;
            }
            .acknowledgement h4 {
              margin: 0 0 10px 0;
              color: #2c3e50;
            }
            .signature-line {
              border-bottom: 1px solid #333;
              width: 200px;
              margin: 20px auto 5px auto;
            }
            .signature-label {
              font-size: 12px;
              color: #6b7280;
            }
            @media print {
              .no-print { display: none !important; }
              body { margin: 0; }
              .payslip-container { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="payslip-container">
            <div class="header">
              <h1>${companyName}</h1>
            </div>

            <div class="employee-info">
              <h2>EMPLOYEE: ${payslipData.employee.name}</h2>
              <p><strong>EMPLOYEE ID:</strong> ${payslipData.employee.id}</p>
              <p><strong>ASSIGNED AT:</strong> ${payslipData.assignment.company}</p>
              <p><strong>DATE COVERED:</strong> ${payslipData.assignment.dateCovered}</p>
            </div>

            <div class="payslip-content">
              <h3 style="text-align: center; margin-bottom: 20px; color: #2c3e50;">13th Month Pay Breakdown</h3>

              <table class="breakdown-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>1st Cutoff</th>
                    <th>2nd Cutoff</th>
                    <th>Month Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${payslipData.breakdown.map(row => `
                    <tr>
                      <td>${row.month}</td>
                      <td>${row.cutoff1 !== '-' ? `₱${row.cutoff1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</td>
                      <td>₱${row.cutoff2.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>₱${row.monthTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `).join('')}
                  <tr>
                    <td><strong>TOTAL</strong></td>
                    <td><strong>₱${totalFirstCutoff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                    <td><strong>₱${totalSecondCutoff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                    <td><strong>₱${totalMonthTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
                  </tr>
                </tbody>
              </table>

              <div class="summary">
                <div class="summary-row">
                  <span>13th MONTH PAY:</span>
                  <span>₱${payslipData.summary.totalBasicPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div class="acknowledgement">
                <h4>ACKNOWLEDGEMENT</h4>
                <p>I HAVE READ AND UNDERSTOOD THE ABOVE COMPUTATIONS OF MY 13th MONTH PAY.</p>
                <div class="signature-line"></div>
                <p class="signature-label">Signature with Date</p>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} sx={{ color: '#12C1DC' }} />
        <Typography variant="h6" sx={{ color: '#6b7280' }}>
          Loading 13th month payslip data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            if (window.location.pathname.startsWith('/print/')) {
              window.close();
            } else {
              navigate(-1);
            }
          }}
          sx={{ bgcolor: '#12C1DC', '&:hover': { bgcolor: '#0FABDB' } }}
        >
          {window.location.pathname.startsWith('/print/') ? 'Close Window' : 'Go Back'}
        </Button>
      </Box>
    );
  }

  if (!payslipData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No 13th month payslip data found
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            if (window.location.pathname.startsWith('/print/')) {
              window.close();
            } else {
              navigate(-1);
            }
          }}
          sx={{ bgcolor: '#12C1DC', '&:hover': { bgcolor: '#0FABDB' } }}
        >
          {window.location.pathname.startsWith('/print/') ? 'Close Window' : 'Go Back'}
        </Button>
      </Box>
    );
  }

  // Render the 13th month payslip content
  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      bgcolor: 'white',
      p: 2
    }}>
      <div
        dangerouslySetInnerHTML={{ __html: generatePrintContent() }}
        style={{ width: '100%' }}
      />
    </Box>
  );
};

export default ThirteenthMonthPayslip;